/**
 * Enhanced performance caching utility for Firebase function calls
 * Provides aggressive caching with smart cache invalidation
 */

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  hits: number;
  projectId?: string;
}

type CacheType = 'project_info' | 'user_data' | 'algolia_keys' | 'features' | 'groups' | 'default';

import { isCacheDisabled } from '@/config/cache-flags';

class PerformanceCache {
  private cache = new Map<string, CacheEntry>();
  private requestPromises = new Map<string, Promise<any>>();
  
  // Different TTLs for different types of data
  private readonly TTL_CONFIG: Record<CacheType, number> = {
    project_info: 10 * 60 * 1000,      // 10 minutes - project info changes rarely
    user_data: 5 * 60 * 1000,          // 5 minutes - user data can change
    algolia_keys: 15 * 60 * 1000,      // 15 minutes - API keys are long-lived
    features: 10 * 60 * 1000,          // 10 minutes - features change rarely
    groups: 5 * 60 * 1000,             // 5 minutes - group membership can change
    default: 2 * 60 * 1000             // 2 minutes default
  };

  /**
   * Get cached data or execute function if cache miss
   */
  async getOrSet<T>(
    cacheKey: string, 
    fetchFn: () => Promise<T>, 
    options: {
      ttl?: number;
      type?: CacheType;
      projectId?: string;
      bypassCache?: boolean;
    } = {}
  ): Promise<T> {
    const { bypassCache = false, type = 'default', projectId } = options;
    const ttl = options.ttl || this.TTL_CONFIG[type];
    
    // Global kill-switch for caching
    if (isCacheDisabled()) {
      console.log(`[PerformanceCache] Global cache disabled. Executing without cache: ${cacheKey}`);
      return await fetchFn();
    }

    if (bypassCache) {
      console.log(`[PerformanceCache] Bypassing cache for: ${cacheKey}`);
      const result = await fetchFn();
      this.set(cacheKey, result, ttl, projectId);
      return result;
    }

    // Check cache first
    const cached = this.get<T>(cacheKey, ttl);
    if (cached) {
      console.log(`[PerformanceCache] Cache hit for: ${cacheKey} (age: ${Date.now() - cached.timestamp}ms)`);
      return cached.data;
    }

    // Check for in-flight request
    const inFlight = this.requestPromises.get(cacheKey);
    if (inFlight) {
      console.log(`[PerformanceCache] Reusing in-flight request: ${cacheKey}`);
      return await inFlight;
    }

    // Execute and cache
    console.log(`[PerformanceCache] Cache miss, executing: ${cacheKey}`);
    const promise = this.executeWithPromiseCache(cacheKey, fetchFn, ttl, projectId);
    return await promise;
  }

  private async executeWithPromiseCache<T>(
    cacheKey: string, 
    fetchFn: () => Promise<T>,
    ttl: number,
    projectId?: string
  ): Promise<T> {
    const startTime = performance.now();
    const promise = fetchFn();
    
    // Store the promise to prevent duplicate requests
    this.requestPromises.set(cacheKey, promise);

    try {
      const result = await promise;
      const duration = performance.now() - startTime;
      
      console.log(`[PerformanceCache] Function completed in ${duration.toFixed(2)}ms: ${cacheKey}`);
      
      // Cache the result
      this.set(cacheKey, result, ttl, projectId);
      
      return result;
    } catch (error) {
      console.error(`[PerformanceCache] Function failed: ${cacheKey}`, error);
      throw error;
    } finally {
      // Always clean up the promise
      this.requestPromises.delete(cacheKey);
    }
  }

  private get<T>(key: string, maxAge: number): CacheEntry<T> | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const age = Date.now() - entry.timestamp;
    if (age > maxAge) {
      this.cache.delete(key);
      return null;
    }

    // Track cache hits
    entry.hits++;
    return entry;
  }

  private set<T>(key: string, data: T, ttl: number, projectId?: string): void {
    if (isCacheDisabled()) {
      // Do not persist entries when globally disabled
      return;
    }
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      hits: 0,
      projectId
    });

    // Auto-cleanup old entries periodically
    if (this.cache.size > 100) {
      this.cleanup();
    }
  }

  /**
   * Clear cache entries for a specific project
   */
  clearProject(projectId: string): void {
    let cleared = 0;
    for (const [key, entry] of this.cache.entries()) {
      if (entry.projectId === projectId || key.includes(projectId)) {
        this.cache.delete(key);
        cleared++;
      }
    }
    console.log(`[PerformanceCache] Cleared ${cleared} entries for project: ${projectId}`);
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    const size = this.cache.size;
    this.cache.clear();
    this.requestPromises.clear();
    console.log(`[PerformanceCache] Cleared ${size} cache entries`);
  }

  /**
   * Remove expired entries
   */
  cleanup(): void {
    const now = Date.now();
    let removed = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      // Use different TTLs based on key patterns
      let ttl = this.TTL_CONFIG.default;
      for (const [type, typeTtl] of Object.entries(this.TTL_CONFIG)) {
        if (key.includes(type)) {
          ttl = typeTtl;
          break;
        }
      }
      
      if (now - entry.timestamp > ttl) {
        this.cache.delete(key);
        removed++;
      }
    }
    
    if (removed > 0) {
      console.log(`[PerformanceCache] Cleaned up ${removed} expired entries`);
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const stats = {
      size: this.cache.size,
      inFlightRequests: this.requestPromises.size,
      entries: [] as Array<{ key: string; age: number; hits: number; projectId?: string }>
    };

    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      stats.entries.push({
        key: key.length > 50 ? key.substring(0, 47) + '...' : key,
        age: now - entry.timestamp,
        hits: entry.hits,
        projectId: entry.projectId
      });
    }

    return stats;
  }
}

// Global instance
export const performanceCache = new PerformanceCache();

// Auto-cleanup every 5 minutes
setInterval(() => {
  performanceCache.cleanup();
}, 5 * 60 * 1000);

// Expose for debugging
if (typeof window !== 'undefined') {
  (window as any).performanceCache = performanceCache;
}
